import { useTranslations } from 'next-intl';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useStore } from '@/providers/app-store-provider';
import { userPresList } from '@/api/account';
import useResponsive from '@/hooks/useResponsive';
import logger from '@/utils/client-logger';
import { Prescription } from '@/api/types/account';
import { handlerPrescriptionData } from '@/utils/prescription';
import SavedPrescriptionItem from './SavedPrescriptionItem';
import InfiniteScroll from '@/components/InfiniteScroll';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EditOutlined } from '@zeelool/icon';
import { getApiPrescriptionType } from '@/app/_lens-components/utils';
import { EPrescriptionType } from '@/utils/enums/goods';
import useTrackEditPrescription from '../../services/useTrackEditPrescription';
import { CustomerPrescription } from '@/app/_lens-components/types';

// 定义暴露给父组件的方法接口
export interface SavedPrescriptionRef {
  getSelectedPrescription: () => CustomerPrescription | null;
}

interface SavedPrescriptionProps {
  prescriptionType: EPrescriptionType;
  /** 当前步骤是否激活（用于在步骤展示时可靠触发浏览埋点，避免 useInView 在 hidden 父节点下不触发） */
  isStepActive?: boolean;
  onEditPrescription: () => void;
}

const SavedPrescription = forwardRef<SavedPrescriptionRef, SavedPrescriptionProps>(
  ({ prescriptionType, isStepActive = false, onEditPrescription }, ref) => {
    const t = useTranslations('lens-process');
    const presT = useTranslations('account.myPrescriptions');
    const { isMobile } = useResponsive();
    const token = useStore((state) => state.token);

    const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
    const [selectedPrescription, setSelectedPrescription] = useState<Prescription>(null);
    const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
    const [pagination, setPagination] = useState({
      page: 1,
      limit: 10,
      total: 0,
    });
    const { trackSavedPrescriptionsView, trackClickSavedPrescription, trackEditSavedPrescription } =
      useTrackEditPrescription();
    const [loading, setLoading] = useState(false);
    const isFirstRef = useRef(true);
    const isTriggered = useRef(false);

    const { isPc } = useResponsive();
    const getPrescriptions = async (page = 1, isFirst = false) => {
      try {
        setLoading(true);
        setIsGlobalLoading(true);
        const { data } = await userPresList({
          limit: 10,
          page,
          prescription_type: getApiPrescriptionType(prescriptionType),
          order: 'used_time',
        });

        if (data?.list?.data) {
          const arr = data.list.data.map((item) => {
            return handlerPrescriptionData(item);
          });
          setPrescriptions((prev) => [...prev, ...arr]);
          setPagination((prev) => ({
            ...prev,
            total: data.list.total,
          }));
          if (isFirst) {
            setSelectedPrescription(arr[0]);
          }
          // setPrescriptionTotal(response.data.list.total);
          if (isPc) {
            window.scrollTo({ top: 0 });
          }
          if (isFirstRef.current) {
            isFirstRef.current = false;
          }
        }
      } catch (error) {
        logger.error('获取处方列表失败', error);
      } finally {
        setLoading(false);
        setIsGlobalLoading(false);
      }
    };
    useEffect(() => {
      if (!token) return;
      getPrescriptions(1, isFirstRef.current);
    }, [token]);

    // 使用 useImperativeHandle 暴露方法给父组件
    useImperativeHandle(
      ref,
      () => ({
        getSelectedPrescription: () => selectedPrescription,
      }),
      [selectedPrescription],
    );
    const checkSelectedPrescription = (pres: Prescription) => {
      return selectedPrescription?.id === pres.id;
    };
    const genCustomHeader = (prescription) => {
      const { id, name, updated_at, prescription_type, prescription_type_name } = prescription;
      return (
        <>
          <div
            className={`w-full flex justify-between leading-[24px] ${isMobile ? 'items-start' : 'items-center'}`}
          >
            <div className={`flex gap-12 ${isMobile ? 'items-start' : 'items-center'}`}>
              <ZeeloolCheckBox
                checked={selectedPrescription?.id === id}
                sx={{
                  '&.MuiCheckbox-root': {
                    padding: 0,
                    span: {
                      borderRadius: '50%',
                    },
                  },
                }}
                className="f16"
              />
              <div className={`flex ${isMobile ? ' flex-col gap-6' : 'items-center gap-12'}`}>
                <strong className="f18 font-bs">{prescription.name}</strong>
                <div
                  className={`flex items-center ${isMobile ? ' flex-row-reverse  gap-10' : 'gap-12'}`}
                >
                  <time className="text-14 font-br text-secondary-text">{updated_at}</time>
                  <span
                    className={`text-12 font-br text-primary-purple bg-[#0006cf1a]  overflow-hidden px-8 leading-[24px] rounded-12`}
                  >
                    {getPrescriptionType(
                      prescription_type,
                      prescription_type_name || prescription_type,
                    )}
                  </span>
                </div>
              </div>
            </div>
            {checkSelectedPrescription(prescription) && (
              <div
                className="flex items-center gap-4 cursor-pointer"
                onClick={(e) => {
                  trackEditSavedPrescription(name);
                  onEditPrescription();
                  e.stopPropagation();
                }}
              >
                <EditOutlined className="!text-16 text-[#808080]" />
                <span className="f14 font-br text-primary-black">{presT('edit')}</span>
              </div>
            )}
          </div>
        </>
      );
    };
    const getPrescriptionType = (str: string, prescriptionTypeName: string) => {
      if (str === 'Sunglasses Prescription') {
        return presT('prescriptionTypes.sunglasses');
      }

      return prescriptionTypeName;
    };
    const handleReachBottom = () => {
      const next = pagination.page + 1;
      setPagination({ ...pagination, page: next });
      getPrescriptions(next, false);
    };
    // 步骤激活时触发浏览埋点（父级用 hidden/block 切换，useInView 在 hidden 下不可靠，改为依赖 isStepActive）
    useEffect(() => {
      if (isStepActive && !isTriggered.current) {
        trackSavedPrescriptionsView();
        isTriggered.current = true;
      }
    }, [isStepActive]);
    return (
      <div className={`${isMobile ? 'px-14' : ''}`}>
        <div className={`xl:!pb-[0px] ${stepCommonClassName.stepTitle} !pl-0 xl:mb-[24px]`}>
          {t('choosePrescription')}
        </div>
        <section className={`flex flex-col gap-[16px]`}>
          {prescriptions.map((prescription) => (
            <SavedPrescriptionItem
              key={prescription.id}
              wrapperClassName={
                checkSelectedPrescription(prescription) ? 'border-primary-purple' : 'cursor-pointer'
              }
              isShowForm={selectedPrescription?.id === prescription.id}
              prescription={prescription}
              customHeader={() => genCustomHeader(prescription)}
              onSelectPrescription={() => {
                setSelectedPrescription(
                  checkSelectedPrescription(prescription) ? null : prescription,
                );
                trackClickSavedPrescription({
                  save_prescription_name: prescription.name,
                  check_way: checkSelectedPrescription(prescription) ? 'Cancel' : 'Checked',
                });
              }}
            />
          ))}
        </section>
        <InfiniteScroll
          loading={loading}
          onReachBottom={handleReachBottom}
          page={pagination.page}
          total={pagination.total}
          limit={pagination.limit}
          showNoMore={false}
        />
      </div>
    );
  },
);

SavedPrescription.displayName = 'SavedPrescription';

export default SavedPrescription;
